import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {type AnnotationItem} from '../../components/vis/annotations';

type IconPositioningContextType = {
  axisWidth: number | undefined;
  hasAfterIssues: boolean;
  hasBeforeIssues: boolean;
  marginLeft: number;
  marginRight: number;
  xMin: number;
  xMax: number;
  positionedIssues: Record<string, number>;
  sortedPositionedIssues: [string, number][];
  updateIconPositions: (
    iconPositions: Partial<{
      marginLeft: number;
      marginRight: number;
      xMin: number;
      xMax: number;
    }>
  ) => void;
};

export const IconPositioningContext = createContext<IconPositioningContextType>(
  {
    axisWidth: undefined,
    hasAfterIssues: false,
    hasBeforeIssues: false,
    marginLeft: 0,
    marginRight: 0,
    xMin: 0,
    xMax: 0,
    positionedIssues: {},
    sortedPositionedIssues: [],
    updateIconPositions: () => {},
  }
);

export const IconPositioningContextProvider = ({
  children,
  issues,
  width,
}: {
  children: React.ReactNode;
  issues: AnnotationItem[];
  width: number;
}) => {
  const [iconPositions, setIconPositions] = useState<
    Omit<
      IconPositioningContextType,
      | 'positionedIssues'
      | 'sortedPositionedIssues'
      | 'updateIconPositions'
      | 'hasAfterIssues'
      | 'hasBeforeIssues'
      | 'axisWidth'
    >
  >({
    marginLeft: 0,
    marginRight: 0,
    xMin: 0,
    xMax: 0,
  });
  const updateIconPositions = useCallback(
    (
      newPositions: Partial<{
        marginLeft: number;
        marginRight: number;
        xMin: number;
        xMax: number;
      }>
    ) => {
      const updateConditions = [
        newPositions.marginLeft !== iconPositions.marginLeft,
        newPositions.marginRight !== iconPositions.marginRight,
        newPositions.xMin !== iconPositions.xMin,
        newPositions.xMax !== iconPositions.xMax,
      ].some(condition => condition);

      if (updateConditions) {
        setIconPositions(prev => ({
          ...prev,
          ...newPositions,
        }));
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const axisWidth = width
    ? width - iconPositions.marginLeft - iconPositions.marginRight
    : undefined;

  const positionedIssues = useMemo(() => {
    if (axisWidth) {
      return (issues ?? []).reduce(
        (acc, issue) => {
          // Only position issues that are within the current visible domain
          if (
            issue.xValue <= iconPositions.xMin ||
            issue.xValue >= iconPositions.xMax
          ) {
            return acc; // Skip issues outside the visible range
          }

          const range = iconPositions.xMax - iconPositions.xMin;

          // Handle division by zero - when all points are at the same x-value
          const issuePosition =
            range === 0
              ? 0 // Place icons at the start when there's no range
              : (issue.xValue - iconPositions.xMin) / range;

          const shift = issuePosition * axisWidth;

          acc[issue.id] = iconPositions.marginLeft + shift;
          return acc;
        },
        {} as Record<string, number>
      );
    } else {
      return {};
    }
  }, [axisWidth, issues, iconPositions]);

  const value = useMemo(() => {
    return {
      axisWidth,
      ...iconPositions,
      hasBeforeIssues: issues.some(issue => issue.status === 'before'),
      hasAfterIssues: issues.some(issue => issue.status === 'after'),
      positionedIssues,
      sortedPositionedIssues: Object.entries(positionedIssues).sort(
        ([_, left], [__, right]) => left - right
      ),
      updateIconPositions,
    };
  }, [axisWidth, iconPositions, issues, updateIconPositions, positionedIssues]);

  return (
    <IconPositioningContext.Provider value={value}>
      {children}
    </IconPositioningContext.Provider>
  );
};

export const useIconPositioningContext = () => {
  return useContext(IconPositioningContext);
};
